/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Y_STEP_UP } from 'constants/map';
import { Point } from '../../types';
import { straightPathFinder } from '../straight-path-finder';
import { Direction, PathFinderParams } from '../types';
import {
    checkInBetweenNodesInXDirection,
    checkInBetweenNodesInYDirection,
    getDirection,
    getNodesList,
    isSamePoint,
    moveHorizontal,
    moveVertical
} from '../utils';

export const pathFinder = ({ source, target, stations, pathNodeList, isLoopBackPath, prevDirection }: PathFinderParams): Point[] => {
    // Return if source and target are on the same axis
    if (source.x === target.x || source.y === target.y) {
        return straightPathFinder({ source, target, stations, pathNodeList, isLoopBackPath, prevDirection });
    }

    const path = [source];
    const stationNodeList = getNodesList(stations);
    const direction: Direction = getDirection({ source, target });
    const commonRecursionParams = {
        target,
        stations,
        isLoopBackPath,
        pathNodeList
    };

    // Move forward on y direction
    if (direction.y !== 0) {
        // Target Y would be 50 point before only or after if the path is loop back
        const targetY = isLoopBackPath ? target.y - Y_STEP_UP : direction.y === -1 ? target.y + Y_STEP_UP : target.y - Y_STEP_UP;

        let point = { ...source };
        while (
            point.y !== targetY &&
            !stations.find(station => isSamePoint(station, moveVertical(point, direction.y))) && // Break if there is any station in between
            !(path.length > 1 && checkInBetweenNodesInYDirection({ source: point, target, direction, nodeList: pathNodeList, count: 2 })) // Break if there is any path in between
        ) {
            point = moveVertical(point, direction.y);
            path.push(point);
        }
    }

    // Move extra vertical steps to avoid opposite horizontal path collision
    if (
        direction.x !== 0 &&
        checkInBetweenNodesInXDirection({
            checkPathInOppositeDirection: true,
            source: path[path.length - 1],
            target: target,
            direction,
            nodeList: pathNodeList,
            count: 2
        })
    ) {
        let point = { ...path[path.length - 1] };
        while (
            !stations.find(station => isSamePoint(station, moveVertical(point, direction.y))) && // Break if there is any station in between
            // Continue going in vertical direction until you don't find any station and path in opposite direction in between
            (checkInBetweenNodesInXDirection({ source: point, target, direction, nodeList: stationNodeList }) ||
                checkInBetweenNodesInXDirection({
                    checkPathInOppositeDirection: true,
                    source: point,
                    target,
                    direction,
                    nodeList: pathNodeList,
                    count: 2
                }))
        ) {
            point = moveVertical(point, direction.y);
            path.push(point);
        }
    }

    if (direction.x !== 0) {
        // Move forward on x direction
        let point = { ...path[path.length - 1] };
        while (
            point.x !== target.x &&
            !stations.find(station => isSamePoint(station, moveHorizontal(point, direction.x))) &&
            !(path.length > 1 && checkInBetweenNodesInXDirection({ source: point, target, direction, nodeList: pathNodeList, count: 2 }))
        ) {
            point = moveHorizontal(point, direction.x);
            path.push(point);
        }
        if (point.y !== target.y) {
            return path.concat(pathFinder({ source: point, prevDirection: { x: direction.x, y: 0 }, ...commonRecursionParams }).slice(1));
        }
    }
    return path;
};
